import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select, Switch,
  message, Popconfirm, Typography, Row, Col, Statistic, Tooltip, Alert
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, TeamOutlined, UserOutlined,
  CheckCircleOutlined, SettingOutlined
} from '@ant-design/icons';
import api from '../../../services/api';
import BackButton from '../../../components/BackButton';

const { Title, Text } = Typography;

const TeamManagement = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/partners/team/');
      setTeamMembers(response.data.results || response.data || []);
    } catch (error) {
      message.error(t('partner.team.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    form.resetFields();
    form.setFieldsValue({
      role: 'STAFF',
      can_manage_inventory: true,
      can_manage_orders: true,
      can_manage_appointments: true,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/partners/team/', values);
      message.success(t('partner.team.addSuccess'));
      setModalVisible(false);
      fetchTeamMembers();
    } catch (error) {
      if (error.response?.data) {
        const errors = error.response.data;
        if (errors.email) {
          message.error(errors.email[0] || errors.email);
        } else if (errors.non_field_errors) {
          message.error(errors.non_field_errors[0]);
        } else if (errors.detail) {
          message.error(errors.detail);
        } else {
          message.error(t('partner.team.addFailed'));
        }
      } else if (!error.errorFields) {
        message.error(t('partner.team.addFailed'));
      }
    }
  };

  const handleRemove = async (memberId) => {
    try {
      await api.delete(`/partners/team/${memberId}/`);
      message.success(t('partner.team.removeSuccess'));
      fetchTeamMembers();
    } catch (error) {
      message.error(t('partner.team.removeFailed'));
    }
  };

  const handlePermissionChange = async (memberId, field, value) => {
    try {
      await api.patch(`/partners/team/${memberId}/`, { [field]: value });
      message.success(t('partner.team.updateSuccess'));
      fetchTeamMembers();
    } catch (error) {
      message.error(t('partner.team.updateFailed'));
    }
  };

  const getRoleTag = (role) => {
    const colors = {
      MANAGER: 'blue',
      STAFF: 'green',
    };
    return (
      <Tag color={colors[role] || 'default'}>
        {role === 'MANAGER' ? t('partner.team.manager') : t('partner.team.staff')}
      </Tag>
    );
  };

  const columns = [
    {
      title: t('partner.team.member'),
      key: 'member',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.user_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.user_email}</Text>
        </Space>
      ),
    },
    {
      title: t('partner.team.role'),
      dataIndex: 'role',
      key: 'role',
      render: (role) => getRoleTag(role),
    },
    {
      title: t('partner.team.inventory'),
      key: 'can_manage_inventory',
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={record.can_manage_inventory}
          onChange={(checked) => handlePermissionChange(record.id, 'can_manage_inventory', checked)}
          size="small"
        />
      ),
    },
    {
      title: t('partner.team.orders'),
      key: 'can_manage_orders',
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={record.can_manage_orders}
          onChange={(checked) => handlePermissionChange(record.id, 'can_manage_orders', checked)}
          size="small"
        />
      ),
    },
    {
      title: t('partner.team.appointments'),
      key: 'can_manage_appointments',
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={record.can_manage_appointments}
          onChange={(checked) => handlePermissionChange(record.id, 'can_manage_appointments', checked)}
          size="small"
        />
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title={t('partner.team.removeConfirm')}
          onConfirm={() => handleRemove(record.id)}
          okText={t('common.yes')}
          cancelText={t('common.no')}
        >
          <Tooltip title={t('common.remove')}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  const canAddMore = teamMembers.length < 2;

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to="/partner/dashboard" label={t('partner.backToDashboard')} />

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('partner.team.totalMembers')}
              value={teamMembers.length}
              suffix="/ 2"
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('partner.team.managers')}
              value={teamMembers.filter(m => m.role === 'MANAGER').length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('partner.team.staffMembers')}
              value={teamMembers.filter(m => m.role === 'STAFF').length}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Info Alert */}
      <Alert
        message={t('partner.team.infoTitle')}
        description={t('partner.team.infoDescription')}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card
        title={
          <Space>
            <TeamOutlined />
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
              {t('partner.team.title')}
            </span>
          </Space>
        }
        extra={
          <Tooltip title={!canAddMore ? t('partner.team.maxReached') : ''}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAddModal}
              disabled={!canAddMore}
            >
              {t('partner.team.addMember')}
            </Button>
          </Tooltip>
        }
      >
        <Table
          columns={columns}
          dataSource={teamMembers}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{ emptyText: t('partner.team.noMembers') }}
        />
      </Card>

      {/* Add Member Modal */}
      <Modal
        title={t('partner.team.addMember')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label={t('partner.team.email')}
            rules={[
              { required: true, message: t('partner.team.emailRequired') },
              { type: 'email', message: t('partner.team.emailInvalid') },
            ]}
            extra={t('partner.team.emailHelp')}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="role"
            label={t('partner.team.role')}
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="MANAGER">{t('partner.team.manager')}</Select.Option>
              <Select.Option value="STAFF">{t('partner.team.staff')}</Select.Option>
            </Select>
          </Form.Item>

          <Title level={5} style={{ marginTop: 16, marginBottom: 12 }}>
            <SettingOutlined /> {t('partner.team.permissions')}
          </Title>

          <Form.Item
            name="can_manage_inventory"
            valuePropName="checked"
          >
            <Space>
              <Switch />
              <Text>{t('partner.team.canManageInventory')}</Text>
            </Space>
          </Form.Item>

          <Form.Item
            name="can_manage_orders"
            valuePropName="checked"
          >
            <Space>
              <Switch />
              <Text>{t('partner.team.canManageOrders')}</Text>
            </Space>
          </Form.Item>

          <Form.Item
            name="can_manage_appointments"
            valuePropName="checked"
          >
            <Space>
              <Switch />
              <Text>{t('partner.team.canManageAppointments')}</Text>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamManagement;
